import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { loginUser, isAuthenticated } from "../utils/auth";
import logo from "../assets/images/logotis.jpg";
import { CircularProgress, Box } from "@mui/material";
import { keyframes } from "@mui/system";
import BackgroundImage from "../assets/images/bgimg.jpg";

// Blink animation keyframes
const blinkAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
`;

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) navigate("/dashboard");
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await axios.post(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/login",
        { username, password }
      );

      const apiBody = response.data["body-json"];

      if (apiBody?.statusCode === 200 && apiBody.body?.token?.access_token) {
        const token = apiBody.body.token.access_token;
        const userData = apiBody.body.data;

        // Use the centralized loginUser utility
        loginUser({ token, user: userData });

        setSuccess("");
        setTimeout(() => {
          navigate("/dashboard");
        });
      } else {
        setError("Invalid login credentials");
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        backgroundImage: `url(${BackgroundImage})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 150,
          height: 70,
          backgroundColor: "#93c5fd",
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 100,
          borderBottomRightRadius: 100,
          animation: `${blinkAnimation} 1.5s infinite`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: 100,
          height: 50,
          backgroundColor: "#93c5fd",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderTopLeftRadius: 100,
          borderTopRightRadius: 100,
          animation: `${blinkAnimation} 1.5s infinite`,
          animationDelay: "0.5s",
        }}
      />

      <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl flex flex-col md:flex-row overflow-hidden">
        <div
          className="w-full md:w-2/5 bg-gradient-to-tr  text-white p-8 flex flex-col items-center justify-center"
          style={{
            background:
              "linear-gradient(to top right,#034a75,#1d98ca, #7fcae2)",
          }}
        >
          <h2 className="text-3xl font-bold mb-4">Welcome</h2>
          <div className="w-32 h-32 bg-white rounded-full mb-4 overflow-hidden">
            <img
              src={logo}
              alt="Logo"
              className="object-contain w-full h-full"
            />
          </div>
          <p>Sign in to continue</p>
        </div>

        <div className="w-full md:w-3/5 p-8">
          <h2 className="text-2xl font-semibold text-center mb-6">Sign in</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-4 py-2 mt-1 focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-4 py-2 mt-1 focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
            {success && (
              <p className="text-green-600 text-sm text-center">{success}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center items-center gap-2 bg-blue-600 text-white font-semibold py-2 rounded-md hover:bg-blue-700 transition duration-200 ${loading ? "cursor-not-allowed opacity-70" : ""
                }`}
            >
              {loading && <CircularProgress size={20} color="inherit" />}
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </Box>
  );
};

export default Login;
